<template>
  <div class="contato-page">
    <HomeHeader />

    <!-- Hero Section com Efeito Parallax -->
    <section class="hero-section">
      <div class="parallax-container">
        <div class="overlay"></div>
        <h1 class="hero-title">{{ t('contact.heroTitle') }}</h1>
        <div class="hero-subtitle">{{ t('contact.heroSubtitle') }}</div>
      </div>
    </section>

    <!-- Seção Principal de Contato -->
    <section class="contact-main">
      <div class="stats-watermark stats-watermark-1"></div>
      <div class="stats-watermark stats-watermark-2"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="100">
          <div class="section-title">
            <span class="accent-line"></span>
            <h2>{{ t('contact.introTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
          <p class="intro-text">
            {{ t('contact.introText') }}
          </p>
        </ScrollReveal>

        <div class="contact-container">
          <!-- Tabs de Navegação -->
          <div class="contact-tabs">
            <ScrollReveal direction="left" :delay="200">
              <button @click="activeSection = 'SAC'" :class="{ active: activeSection === 'SAC' }" class="tab-button">
                <i class="fas fa-headset"></i>
                <span>{{ t('contact.departments.support.title') }}</span>
              </button>

              <button @click="activeSection = 'Comercial'" :class="{ active: activeSection === 'Comercial' }"
                class="tab-button">
                <i class="fas fa-shopping-cart"></i>
                <span>{{ t('contact.departments.commercial.title') }}</span>
              </button>

              <button @click="activeSection = 'Licitação'" :class="{ active: activeSection === 'Licitação' }"
                class="tab-button">
                <i class="fas fa-file-contract"></i>
                <span>{{ t('contact.departments.bidding.title') }}</span>
              </button>

              <button @click="activeSection = 'Financeiro'" :class="{ active: activeSection === 'Financeiro' }"
                class="tab-button">
                <i class="fas fa-chart-line"></i>
                <span>{{ t('contact.departments.financial.title') }}</span>
              </button>

              <button @click="activeSection = 'LGPD'" :class="{ active: activeSection === 'LGPD' }" class="tab-button">
                <i class="fas fa-lock"></i>
                <span>{{ t('contact.departments.lgpd.title') }}</span>
              </button>
            </ScrollReveal>
          </div>

          <!-- Conteúdo dos Tabs -->
          <div class="contact-content">
            <ScrollReveal direction="right" :delay="300">
              <!-- Suporte -->
              <div v-if="activeSection === 'SAC'" class="contact-panel">
                <div class="contact-header">
                  <div class="contact-icon">
                    <i class="fas fa-headset"></i>
                  </div>
                  <h3>{{ t('contact.departments.support.fullTitle') }}</h3>
                </div>

                <div class="contact-info">
                  <div class="info-item email" @click="openEmail('contato@unihospitalar.com.br')">
                    <i class="fas fa-envelope"></i>
                    <p>{{ t('contact.departments.support.email') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <p>{{ t('contact.departments.support.phone') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-clock"></i>
                    <p>{{ t('contact.departments.support.hours') }}</p>
                  </div>
                </div>

                <div class="contact-message">
                  <div class="message-header">
                    <i class="fas fa-paper-plane"></i>
                    <h4>{{ t('contact.messageSection.title') }}</h4>
                  </div>
                  <p>{{ t('contact.messageSection.description') }}</p>
                  <ContatoFormulario :activeSection="activeSection" />
                </div>
              </div>

              <!-- Comercial -->
              <div v-if="activeSection === 'Comercial'" class="contact-panel">
                <div class="contact-header">
                  <div class="contact-icon">
                    <i class="fas fa-shopping-cart"></i>
                  </div>
                  <h3>{{ t('contact.departments.commercial.fullTitle') }}</h3>
                </div>

                <div class="contact-info">
                  <div class="info-item email" @click="openEmail('vendas@unihospitalar.com.br')">
                    <i class="fas fa-envelope"></i>
                    <p>{{ t('contact.departments.commercial.email') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <p>{{ t('contact.departments.commercial.phone') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-clock"></i>
                    <p>{{ t('contact.departments.commercial.hours') }}</p>
                  </div>
                </div>

                <div class="contact-message">
                  <div class="message-header">
                    <i class="fas fa-paper-plane"></i>
                    <h4>{{ t('contact.messageSection.title') }}</h4>
                  </div>
                  <p>{{ t('contact.messageSection.description') }}</p>
                  <ContatoFormulario :activeSection="activeSection" />
                </div>
              </div>

              <!-- Licitação -->
              <div v-if="activeSection === 'Licitação'" class="contact-panel">
                <div class="contact-header">
                  <div class="contact-icon">
                    <i class="fas fa-file-contract"></i>
                  </div>
                  <h3>{{ t('contact.departments.bidding.fullTitle') }}</h3>
                </div>

                <div class="contact-info">
                  <div class="info-item email" @click="openEmail('licitacao@unihospitalar.com.br')">
                    <i class="fas fa-envelope"></i>
                    <p>{{ t('contact.departments.bidding.email') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <p>{{ t('contact.departments.bidding.phone') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-clock"></i>
                    <p>{{ t('contact.departments.bidding.hours') }}</p>
                  </div>
                </div>

                <div class="contact-message">
                  <div class="message-header">
                    <i class="fas fa-paper-plane"></i>
                    <h4>{{ t('contact.messageSection.title') }}</h4>
                  </div>
                  <p>{{ t('contact.messageSection.description') }}</p>
                  <ContatoFormulario :activeSection="activeSection" />
                </div>
              </div>

              <!-- Financeiro -->
              <div v-if="activeSection === 'Financeiro'" class="contact-panel">
                <div class="contact-header">
                  <div class="contact-icon">
                    <i class="fas fa-chart-line"></i>
                  </div>
                  <h3>{{ t('contact.departments.financial.fullTitle') }}</h3>
                </div>

                <div class="contact-info">
                  <div class="info-item email" @click="openEmail('financeiro@unihospitalar.com.br')">
                    <i class="fas fa-envelope"></i>
                    <p>{{ t('contact.departments.financial.email') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <p>{{ t('contact.departments.financial.phone') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-clock"></i>
                    <p>{{ t('contact.departments.financial.hours') }}</p>
                  </div>
                </div>

                <div class="contact-message">
                  <div class="message-header">
                    <i class="fas fa-paper-plane"></i>
                    <h4>{{ t('contact.messageSection.title') }}</h4>
                  </div>
                  <p>{{ t('contact.messageSection.description') }}</p>
                  <ContatoFormulario :activeSection="activeSection" />
                </div>
              </div>

              <!-- LGPD -->
              <div v-if="activeSection === 'LGPD'" class="contact-panel">
                <div class="contact-header">
                  <div class="contact-icon">
                    <i class="fas fa-lock"></i>
                  </div>
                  <h3>{{ t('contact.departments.lgpd.fullTitle') }}</h3>
                </div>

                <div class="contact-info">
                  <div class="info-item email" @click="openEmail('dpo@unihospitalar.com.br')">
                    <i class="fas fa-envelope"></i>
                    <p>{{ t('contact.departments.lgpd.email') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-phone"></i>
                    <p>{{ t('contact.departments.lgpd.phone') }}</p>
                  </div>
                  <div class="info-item">
                    <i class="fas fa-clock"></i>
                    <p>{{ t('contact.departments.lgpd.hours') }}</p>
                  </div>
                </div>

                <div class="contact-message">
                  <div class="message-header">
                    <i class="fas fa-paper-plane"></i>
                    <h4>{{ t('contact.messageSection.title') }}</h4>
                  </div>
                  <p>{{ t('contact.messageSection.description') }}</p>
                  <ContatoFormulario :activeSection="activeSection" />
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
    </section>

    <!-- Seção de Localização -->
    <section class="location-section">
      <div class="stats-watermark-center"></div>
      <div class="container">
        <ScrollReveal direction="bottom" :delay="200">
          <div class="section-title light">
            <span class="accent-line"></span>
            <h2>{{ t('contact.locationsTitle') }}</h2>
            <span class="accent-line"></span>
          </div>
        </ScrollReveal>

        <div class="location-tabs">
          <ScrollReveal direction="bottom" :delay="250">
            <div class="location-selector">
              <div v-for="(branch, index) in translatedBranches" :key="index" @click="setActiveBranch(index)"
                :class="['location-tab', { active: activeBranch === index }]">
                <div class="tab-icon">
                  <i :class="branch.icon"></i>
                </div>
                <div class="tab-info">
                  <h4>{{ branch.name }}</h4>
                  <span>{{ branch.city }}</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>

        <div class="location-container">
          <ScrollReveal direction="left" :delay="300">
            <div class="location-map">
              <transition name="fade" mode="out-in">
                <iframe :key="activeBranch" :src="branches[activeBranch].mapUrl" width="100%" height="450"
                  style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade">
                </iframe>
              </transition>
            </div>
          </ScrollReveal>

          <ScrollReveal direction="right" :delay="400">
            <div class="location-info">
              <transition name="slide" mode="out-in">
                <div :key="activeBranch" class="location-card">
                  <div class="location-header">
                    <i :class="translatedBranches[activeBranch].icon"></i>
                    <h3>{{ translatedBranches[activeBranch].name }}</h3>
                  </div>
                  <div class="location-details">
                    <p><strong>{{ t('contact.locationDetails.address') }}</strong> {{
                      translatedBranches[activeBranch].address }}</p>
                    <p><strong>{{ t('contact.locationDetails.cep') }}</strong> {{ translatedBranches[activeBranch].cep
                      }}</p>
                    <p><strong>{{ t('contact.locationDetails.cnpj') }}</strong> {{
                      translatedBranches[activeBranch].cnpj }}</p>
                    <p><strong>{{ t('contact.locationDetails.phone') }}</strong> {{
                      translatedBranches[activeBranch].phone }}</p>
                    <p><strong>{{ t('contact.locationDetails.email') }}</strong> {{
                      translatedBranches[activeBranch].email }}</p>
                    <p><strong>{{ t('contact.locationDetails.hours') }}</strong> {{
                      translatedBranches[activeBranch].hours }}</p>
                  </div>
                  <div class="location-actions">
                    <a :href="branches[activeBranch].directionsUrl" target="_blank" class="directions-button">
                      <i class="fas fa-directions"></i>
                      <span>{{ t('contact.locationDetails.directions') }}</span>
                    </a>
                  </div>
                </div>
              </transition>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>

    <HomeFooter />
  </div>
</template>

<script>
import HomeHeader from '@/components/HomeHeader.vue';
import HomeFooter from '@/components/HomeFooter.vue';
import ContatoFormulario from '@/components/ContatoFormulario.vue';
import { useContactTranslation } from '@/composables/useContactTranslation.js';

export default {
  name: 'ContatoPage',
  components: {
    ContatoFormulario,
    HomeHeader,
    HomeFooter
  },
  setup() {
    const { t, currentLanguage } = useContactTranslation()

    return {
      t,
      currentLanguage
    }
  },
  data() {
    return {
      activeSection: 'SAC',
      selectedLanguage: 'pt',
      sidebarOpen: false,
      activeBranch: 0, // Filial ativa inicialmente (Recife)
      branches: [
        {
          icon: 'fas fa-map-marker-alt',
          mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d246.91!2d-34.8951233!3d-8.0623376!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7ab1be2a5d9b3d7%3A0x123456789!2sR.%20Alagoas%2C%20253%20-%20Ipsep%2C%20Recife%20-%20PE%2C%2051350-560%2C%20Brasil!5e0!3m2!1spt-BR!2sbr!4v1699999999999!5m2!1spt-BR!2sbr',
          directionsUrl: 'https://www.google.com/maps/dir//R.+Alagoas,+253+-+Ipsep,+Recife+-+PE,+51350-000'
        },
        {
          icon: 'fas fa-map-marker-alt',
          mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d124.37358531027889!2d-38.58232312426936!3d-3.8271872440876074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x7c74c7a7a7a7a7a7%3A0x9e3e5b0d8d3d8d3d!2sR.%20Fl%C3%B4res%2C%2038%20-%20Cajazeiras%2C%20Fortaleza%20-%20CE%2C%2060864-510!5e0!3m2!1spt-BR!2sbr!4v1625151234567!5m2!1spt-BR!2sbr',
          directionsUrl: 'https://www.google.com/maps/dir//R.+Fl%C3%B4res,+38+-+Cajazeiras,+Fortaleza+-+CE,+60864-510'
        },
        {
          icon: 'fas fa-map-marker-alt',
          mapUrl: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d115.29363478149057!2d-46.92011492368744!3d-23.55290096159868!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94cf03b8b1b8b8b9%3A0x9e3e5b0d8d3d8d3d!2sR.%20Pinhal%2C%20165%20-%20Jardim%20Sabi%C3%A1%2C%20Cotia%20-%20SP%2C%2006716-575!5e0!3m2!1spt-BR!2sbr!4v1625151234567!5m2!1spt-BR!2sbr',
          directionsUrl: 'https://www.google.com/maps/dir//R.+Pinhal,+165+-+Jardim+Sabiá,+Cotia+-+SP,+06716-575'
        }
      ]
    }
  },
  computed: {
    translatedBranches() {
      return [
        {
          icon: 'fas fa-map-marker-alt',
          name: this.t('contact.branches.recife.name'),
          city: this.t('contact.branches.recife.city'),
          address: this.t('contact.branches.recife.address'),
          cep: this.t('contact.branches.recife.cep'),
          cnpj: this.t('contact.branches.recife.cnpj'),
          phone: this.t('contact.branches.recife.phone'),
          email: this.t('contact.branches.recife.email'),
          hours: this.t('contact.branches.recife.hours')
        },
        {
          icon: 'fas fa-map-marker-alt',
          name: this.t('contact.branches.fortaleza.name'),
          city: this.t('contact.branches.fortaleza.city'),
          address: this.t('contact.branches.fortaleza.address'),
          cep: this.t('contact.branches.fortaleza.cep'),
          cnpj: this.t('contact.branches.fortaleza.cnpj'),
          phone: this.t('contact.branches.fortaleza.phone'),
          email: this.t('contact.branches.fortaleza.email'),
          hours: this.t('contact.branches.fortaleza.hours')
        },
        {
          icon: 'fas fa-map-marker-alt',
          name: this.t('contact.branches.saopaulo.name'),
          city: this.t('contact.branches.saopaulo.city'),
          address: this.t('contact.branches.saopaulo.address'),
          cep: this.t('contact.branches.saopaulo.cep'),
          cnpj: this.t('contact.branches.saopaulo.cnpj'),
          phone: this.t('contact.branches.saopaulo.phone'),
          email: this.t('contact.branches.saopaulo.email'),
          hours: this.t('contact.branches.saopaulo.hours')
        }
      ]
    }
  },
  methods: {
    changeLanguage(event) {
      const value = typeof event === 'string' ? event : event.target.value;
      this.selectedLanguage = value;
      console.log(`Idioma selecionado: ${value}`);
    },
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    setActiveBranch(index) {
      this.activeBranch = index;
    },
    openEmail(email) {
    window.location.href = `mailto:${email}`;
  }
  },
}
</script>

<style scoped>
/* Estilos Gerais */
.contato-page {
  font-family: 'Matter', 'Segoe UI', Arial, sans-serif;
  color: #333;
  line-height: 1.6;
  overflow-x: hidden;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

section {
  padding: 80px 0;
}

/* Hero Section com Parallax */
.hero-section {
  position: relative;
  height: 90vh;
  min-height: 500px;
  overflow: hidden;
  border-bottom: 4px solid #931414;
}

.parallax-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/header-contato.jfif');
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: white;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(rgba(147, 20, 20, 0.7), rgba(0, 0, 0, 0.7));
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 20px;
  position: relative;
  z-index: 2;
  text-transform: uppercase;
  letter-spacing: 2px;
  animation: fadeInUp 1.5s ease-out;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 300;
  position: relative;
  z-index: 2;
  animation: fadeInUp 1.5s ease-out 0.3s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Seção Principal de Contato */
.contact-main {
  background-color: #f0f0f0;
  position: relative;
  overflow: hidden;
}

/* Símbolo da marca duplicado ao fundo, desmaiado, nos dois cantos opostos */
.stats-watermark {
  position: absolute;
  top: 0;
  width: 700px;
  height: 700px;
  background-image: url('@/assets/uni-simbolo-outline.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  opacity: 0.16;
  pointer-events: none;
  z-index: 0;
}

.stats-watermark-1 {
  left: -181px;
  top: -203px;
  mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to bottom right, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

.stats-watermark-2 {
  right: -203px;
  left: auto;
  top: auto;
  bottom: -226px;
  mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
  -webkit-mask-image: linear-gradient(to top left, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.6) 55%, transparent 85%);
}

@media (min-width: 769px) and (max-height: 820px) {
  .stats-watermark-2 {
    display: none;
  }
}

@media (max-width: 768px) {
  .stats-watermark-2 {
    display: none;
  }
}

.contact-main::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(240, 240, 240, 0.05) 0px, rgba(240, 240, 240, 0.05) 1px, transparent 1px, transparent 14px);
  z-index: 0;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  text-align: center;
}

.section-title h2 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #931414;
  margin: 0 20px;
}

.section-title.light h2 {
  color: #ffffff;
}

.accent-line {
  height: 3px;
  width: 70px;
  background-color: #931414;
}

.section-title.light .accent-line {
  background-color: #ffffff;
}

.intro-text {
  font-size: 1.2rem;
  max-width: 800px;
  margin: 0 auto 40px;
  color: #555;
  line-height: 1.8;
  text-align: center;
}

/* Container de Contato */
.contact-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-top: 50px;
  position: relative;
  z-index: 1;
}

/* Tabs de Navegação */
.contact-tabs {
  width: 100%;
  margin-bottom: 30px;
}

.contact-tabs>div {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 15px;
  flex-wrap: wrap;
}

.tab-button {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.75), rgba(255, 255, 255, 0.55));
  backdrop-filter: blur(20px) saturate(160%);
  -webkit-backdrop-filter: blur(20px) saturate(160%);
  border: 1px solid rgba(147, 20, 20, 0.25);
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  color: #555;
  font-weight: 600;
  font-size: 1.1rem;
  min-width: 150px;
}

.tab-button i {
  font-size: 1.5rem;
  margin-right: 15px;
  color: #931414;
  transition: all 0.3s ease;
}

.tab-button:hover:not(.active) {
  transform: translateY(-5px);
  border-color: rgba(147, 20, 20, 0.4);
  animation: uni-btn-pulse-rubi 1.5s infinite;
}

@keyframes uni-btn-pulse-rubi {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.35);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

/* .tab-button.active está na regra global de glass, mas o fundo aqui
   (.tab-button base) é branco chapado, então precisa de opacidade alta
   pra não ficar sujo/desbotado */
.tab-button.active {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(147, 20, 20, 0.32), rgba(71, 4, 4, 0.32)) !important;
  backdrop-filter: blur(20px) saturate(200%) !important;
  -webkit-backdrop-filter: blur(20px) saturate(200%) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.tab-button.active::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 60%);
  pointer-events: none;
}

.tab-button.active span {
  position: relative;
    -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

.tab-button.active i {
  position: relative;
  background-image: none !important;
  -webkit-background-clip: initial !important;
  background-clip: initial !important;
  -webkit-text-fill-color: #470404 !important;
  color: #470404 !important;
}

/* Conteúdo dos Tabs */
.contact-content {
  width: 100%;
}

/* Responsividade para os tabs */
@media (max-width: 768px) {
  .tab-button {
    min-width: calc(50% - 10px);
    flex: 0 0 calc(50% - 10px);
    padding: 15px 10px;
  }
}

@media (max-width: 576px) {
  .tab-button {
    min-width: 100%;
    flex: 0 0 100%;
    margin-bottom: 10px;
  }
}

.contact-panel {
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.contact-header {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  padding: 30px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.contact-icon {
  font-size: 2.5rem;
  background: rgba(255, 255, 255, 0.2);
  width: 70px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
}

.contact-header h3 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
}

.contact-info {
  padding: 30px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  border-bottom: 1px solid #f0f0f0;
  justify-content: center;
}

.info-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  flex: 1;
  min-width: 250px;
  text-align: center;
}

.info-item.email {
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 10px;
  border-radius: 8px;
}

.info-item.email:hover {
  background-color: rgba(147, 20, 20, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-item.email p {
  color: #931414;
  text-decoration: underline;
  font-weight: 600;
}

.info-item.email:hover p {
  color: #470404;
}

.info-item i {
  font-size: 1.5rem;
  color: #931414;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f0f0f0;
  border-radius: 50%;
}

.info-item p {
  margin: 0;
  font-size: 1.1rem;
  color: #555;
}

.contact-message {
  padding: 30px;
}

.message-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  margin-bottom: 20px;
  width: 100%;
}

.message-header i {
  font-size: 1.5rem;
  color: #931414;
  line-height: 1;
}

.message-header h4 {
  font-size: 1.3rem;
  background: linear-gradient(-135deg, #300303, #931414);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.contact-message p {
  color: #696969;
  margin-bottom: 30px;
}

/* Seção de Localização */
.location-section {
  background: linear-gradient(135deg, #931414, #470404);
  color: white;
  position: relative;
  overflow: hidden;
  padding: 80px 0;
  z-index: 1;
}

.location-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.08) 0px, rgba(255, 255, 255, 0.08) 1px, transparent 1px, transparent 14px);
  opacity: 0.5;
}

/* Símbolo da marca único, centralizado e expandido (fundo sólido vermelho) */
.stats-watermark-center {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('@/assets/uni-simbolo-outline-branco.svg');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 1100px;
  opacity: 0.14;
  pointer-events: none;
  z-index: 0;
}

.location-section ::selection {
  background-color: Highlight;
  color: HighlightText;
}

.location-container {
  display: flex;
  gap: 40px;
  position: relative;
  flex-direction: column;
  z-index: 1;
}

.location-map {
  flex: 3;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.location-info {
  flex: 2;
}

.location-card {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  backdrop-filter: blur(5px);
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.location-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
}

.location-header i {
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.9);
}

.location-header h3 {
  font-size: 1.8rem;
  color: white;
  margin: 0;
}

.location-details p {
  margin: 10px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
}

/* Estilos para a seção de localização com tabs */
.location-tabs {
  margin-bottom: 40px;
}

.location-selector {
  display: flex;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;
  max-width: 900px;
  margin: 0 auto;
}

.location-tab {
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(5px);
  padding: 15px 25px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.2);
  min-width: 250px;
}

.location-tab h4 {
  letter-spacing: 0.15cap;
}

.location-tab:hover {
  transform: translateY(-5px);
  background: rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.location-tab.active {
  background: rgba(255, 255, 255, 0.25);
  border-color: #ffffff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  transform: translateY(-5px);
}

.tab-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  color: white;
  flex-shrink: 0;
}

.location-tab.active .tab-icon {
  background: rgba(255, 255, 255, 0.3);
}

.tab-info {
  flex: 1;
}

.tab-info h4 {
  margin: 0 0 5px;
  font-size: 1.1rem;
  color: white;
  font-weight: 600;
}

.tab-info span {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
}

.location-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
}

.location-map {
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  position: relative;
  height: 450px;
}

.location-map iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 15px;
}

.location-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
  height: 100%;
  display: flex;
  flex-direction: column;
}

.location-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.location-header i {
  font-size: 2rem;
  color: white;
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.location-header h3 {
  font-size: 1.8rem;
  color: white;
  margin: 0;
  font-weight: 700;
}

.location-details {
  flex: 1;
}

.location-details p {
  margin: 12px 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  line-height: 1.6;
}

.location-details strong {
  color: white;
  font-weight: 600;
}

.location-actions {
  margin-top: 25px;
  display: flex;
  justify-content: flex-end;
}

.directions-button {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.16);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 12px 25px;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  cursor: pointer;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.25);
}

.directions-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.directions-button i {
  font-size: 1.2rem;
}

/* Animações para transições */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.5s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* Ajustes específicos para telas grandes */
@media (min-width: 1200px) {
  .location-container {
    max-width: 1200px;
    flex-direction: column;
    margin: 0 auto;
  }

  .location-map {
    flex: 2;
    min-height: 500px;
    /* Altura mínima para o mapa */
  }

  .location-map iframe {
    height: 500px;
    /* Altura fixa para o iframe em telas grandes */
  }

  .location-info {
    flex: 1;
    display: flex;
    align-items: center;
  }

  .location-card {
    width: 100%;
    padding: 40px;
  }

  .location-details p {
    font-size: 1.2rem;
    margin: 15px 0;
  }
}

/* Responsividade */
@media (max-width: 1200px) {
  .container {
    padding: 0 40px;
  }

  .location-container {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .location-map {
    order: 2;
  }

  .location-info {
    order: 1;
  }
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 3rem;
  }

  .hero-subtitle {
    font-size: 1.3rem;
  }

  section {
    padding: 60px 0;
  }

  .contact-container {
    flex-direction: column;
  }

  .tab-button {
    flex: 1;
    min-width: 150px;
    justify-content: center;
  }

  .tab-button i {
    margin-right: 10px;
  }

  .location-container {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .hero-section {
    height: 60vh;
    min-height: 400px;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .section-title h2 {
    font-size: 2rem;
  }

  .intro-text {
    font-size: 1.1rem;
  }

  .tab-button {
    padding: 15px;
    font-size: 0.9rem;
  }

  .tab-button i {
    font-size: 1.2rem;
  }

  .contact-header {
    padding: 20px;
  }

  .contact-icon {
    width: 50px;
    height: 50px;
    font-size: 1.8rem;
  }

  .contact-header h3 {
    font-size: 1.5rem;
  }

  .info-item {
    min-width: 100%;
  }

  .banner-container {
    top: 82px;
    padding-top: 0px;
  }

  .location-selector {
    flex-direction: column;
    align-items: center;
  }

  .location-tab {
    width: 100%;
    max-width: 400px;
  }

  .location-header {
    flex-direction: column;
    text-align: center;
  }

  .location-details p {
    font-size: 1rem;
  }

  .location-actions {
    justify-content: center;
  }
}

@media (max-width: 890px) {
  .contact-tabs>div {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .tab-button {
    width: 100%;
    min-width: auto;
  }
}

@media (max-width: 576px) {
  .container {
    padding: 0 20px;
  }

  .hero-title {
    font-size: 2rem;
  }

  .hero-subtitle {
    font-size: 1.1rem;
  }

  .section-title h2 {
    font-size: 1.8rem;
  }

  .accent-line {
    width: 40px;
  }

  .tab-button {
    min-width: 100%;
    margin-bottom: 10px;
  }

  .contact-tabs>div {
    display: grid;
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .contact-info {
    padding: 20px;
  }

  .contact-message {
    padding: 20px;
  }

  .banner-overlay h1 {
    font-size: 1.5em;
  }

  .location-tab {
    padding: 12px 15px;
  }

  .tab-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .tab-info h4 {
    font-size: 1rem;
  }

  .location-header i {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  .location-header h3 {
    font-size: 1.5rem;
  }

  .location-map {
    height: 350px;
  }
}

/* Animações adicionais */
@keyframes float {
  0% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }

  100% {
    transform: translateY(0px);
  }
}

.contact-icon i,
.location-header i {
  animation: float 3s ease-in-out infinite;
}

/* Animação para os botões de tab quando em hover */
.tab-button:hover i {
  transform: scale(1.2);
  transition: transform 0.3s ease;
}

/* Animação para o mapa */
.location-map {
  transition: transform 0.3s ease;
}

.location-map:hover {
  transform: scale(1.02);
}

/* Efeito de destaque para os itens de informação */
.info-item:hover i {
  background-color: #931414;
  color: white;
  transition: all 0.3s ease;
}

/* Ajustes específicos para banner em dispositivos móveis */
@media (max-width: 400px) {
  .banner-overlay h1 {
    font-size: 1.5em;
  }
}

@media (max-width: 768px) {
  .banner-overlay h1 {
    font-size: 2em;
  }
}

@media (max-width: 576px) {
  .banner-overlay h1 {
    font-size: 1.5em;
  }
}

/* Animação para a seção de localização */
.location-details p {
  opacity: 0;
  animation: fadeIn 0.5s forwards;
}

.location-details p:nth-child(1) {
  animation-delay: 0.3s;
}

.location-details p:nth-child(2) {
  animation-delay: 0.6s;
}

.location-details p:nth-child(3) {
  animation-delay: 0.9s;
}

.location-details p:nth-child(4) {
  animation-delay: 1.2s;
}

.location-details p:nth-child(5) {
  animation-delay: 1.5s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Efeito de sombra para os cards */
.contact-panel {
  position: relative;
}

.contact-panel::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.contact-panel:hover::before {
  opacity: 1;
}

/* Efeito de destaque para links dentro do conteúdo */
.contact-message a {
  color: #931414;
  text-decoration: none;
  position: relative;
  transition: color 0.3s ease;
}

.contact-message a::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background-color: #931414;
  transition: width 0.3s ease;
}

.contact-message a:hover {
  color: #470404;
}

.contact-message a:hover::after {
  width: 100%;
}

/* Ajustes para a animação do slideInRight em diferentes tamanhos de tela */
@keyframes slideInRight {
  from {
    transform: translateX(85%);
    opacity: 0;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 480px) {
  @keyframes slideInRight {
    from {
      transform: translateX(10%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .location-tab {
    width: 80%;
  }
}

@media (max-width: 768px) {
  @keyframes slideInRight {
    from {
      transform: translateX(21%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

@media (min-width: 769px) {
  @keyframes slideInRight {
    from {
      transform: translateX(30%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

@media (min-width: 993px) {
  @keyframes slideInRight {
    from {
      transform: translateX(28%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

@media (min-width: 1390px) {
  @keyframes slideInRight {
    from {
      transform: translateX(57%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

@media (min-width: 300px) {
  @keyframes slideInRight {
    from {
      transform: translateX(11%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

@media (min-width: 400px) {
  @keyframes slideInRight {
    from {
      transform: translateX(30%);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
}

/* Efeito de pulsação para os ícones de contato */
@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(147, 20, 20, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(147, 20, 20, 0);
  }
}

.info-item i:hover {
  animation: pulse 1.5s infinite;
}

/* Efeito de transição suave para os painéis de contato */
.contact-panel {
  transition: opacity 0.5s ease, transform 0.5s ease;
  animation: fadeIn 0.5s ease-out;
}

/* Estilo para o formulário de contato dentro do painel */
.contact-message form {
  margin-top: 30px;
}

/* Efeito de hover para o card de localização */
.location-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.location-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

/* Estilo para o iframe do mapa */
.location-map iframe {
  display: block;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.location-map iframe:hover {
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
}

/* Ajustes para dispositivos muito pequenos */
@media (max-width: 350px) {
  .tab-button {
    padding: 10px;
    font-size: 0.8rem;
  }

  .tab-button i {
    font-size: 1rem;
    margin-right: 5px;
  }

  .contact-header h3 {
    font-size: 1.2rem;
  }

  .info-item p {
    font-size: 0.9rem;
  }

  .location-header h3 {
    font-size: 1.4rem;
  }

  .location-details p {
    font-size: 0.9rem;
  }
}
</style>
